import { useEffect, useMemo, useState } from "react";
import { Wallet, Plus, Pencil, Trash2, Clock, CheckCircle2, BadgeDollarSign } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHeader, EmptyState, Spinner, StatCard, EthicsNote } from "../components/common";
import { FormDialog } from "../components/FormDialog";
import { useResource } from "../hooks/useResource";
import { api, brl, fmtDate } from "../lib/api";

const STATUS = [{ value: "pendente", label: "Pendente" }, { value: "aprovado", label: "Aprovado" }, { value: "pago", label: "Pago" }, { value: "cancelado", label: "Cancelado" }];
const STATUS_CLS = { pendente: "text-amber-300 bg-amber-500/10", aprovado: "text-sky-300 bg-sky-500/10", pago: "text-emerald-300 bg-emerald-500/10", cancelado: "text-rose-300 bg-rose-500/10" };

export default function Commissions() {
  const r = useResource("commissions");
  const [campaigns, setCampaigns] = useState([]);
  const [dlg, setDlg] = useState({ open: false, item: null });
  useEffect(() => { api.get("/campaigns").then((x) => setCampaigns(x.data)); }, []);
  const sum = useMemo(() => {
    const s = { pendente: 0, aprovado: 0, pago: 0 };
    r.items.forEach((c) => { if (s[c.status] != null) s[c.status] += c.amount; });
    return s;
  }, [r.items]);

  const fields = [
    { name: "product_title", label: "Produto", required: true },
    { name: "order_id", label: "Nº do pedido", half: true },
    { name: "date", label: "Data", type: "date", required: true, half: true },
    { name: "sale_value", label: "Valor da venda (R$)", type: "number", half: true },
    { name: "amount", label: "Comissão (R$)", type: "number", required: true, half: true },
    { name: "status", label: "Status", type: "select", options: STATUS, default: "pendente", half: true },
    { name: "campaign_id", label: "Campanha", type: "select", placeholder: "Nenhuma", options: campaigns.map((c) => ({ value: c.id, label: c.name })), half: true },
  ];

  return (
    <div>
      <PageHeader eyebrow="Financeiro" title="Comissões" desc="Registre as comissões a partir do relatório oficial do painel de afiliados."
        action={<Button className="btn-gold rounded-full h-11 px-5" onClick={() => setDlg({ open: true, item: null })} data-testid="commission-add-button"><Plus className="h-4 w-4 mr-1" />Registrar</Button>} />
      <div className="grid grid-cols-3 gap-3 mb-5">
        <StatCard label="Pendente" value={brl(sum.pendente)} icon={Clock} testid="commission-sum-pending" />
        <StatCard label="Aprovado" value={brl(sum.aprovado)} icon={CheckCircle2} testid="commission-sum-approved" delay={2} />
        <StatCard label="Pago" value={brl(sum.pago)} icon={BadgeDollarSign} testid="commission-sum-paid" delay={3} />
      </div>
      {r.loading ? <Spinner /> : r.items.length === 0 ? <EmptyState icon={Wallet} title="Nenhuma comissão registrada" /> : (
        <div className="card-lux divide-y divide-zinc-800/80">
          {r.items.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-4" data-testid={`commission-row-${c.id}`}>
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{c.product_title}</div><div className="text-[11px] text-zinc-500">{fmtDate(c.date)} {c.order_id && `• #${c.order_id}`} {c.sale_value != null && `• venda ${brl(c.sale_value)}`}</div></div>
              <span className={`text-[11px] px-2 py-1 rounded-full capitalize ${STATUS_CLS[c.status]}`}>{c.status}</span>
              <div className="font-display font-bold text-white w-24 text-right">{brl(c.amount)}</div>
              <button className="p-1.5 text-zinc-500 hover:text-amber-400" onClick={() => setDlg({ open: true, item: c })} data-testid={`commission-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
              <button className="p-1.5 text-zinc-500 hover:text-rose-400" onClick={() => r.remove(c.id)} data-testid={`commission-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-5"><EthicsNote>Registre apenas valores do seu relatório oficial. Os totais servem para seu controle e não substituem o extrato da Shopee.</EthicsNote></div>
      <FormDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} title={dlg.item ? "Editar comissão" : "Registrar comissão"} fields={fields}
        initial={dlg.item || { status: "pendente", date: new Date().toISOString().slice(0, 10) }} testid="commission-form" onSubmit={(b) => r.save(b, dlg.item?.id)} />
    </div>
  );
}
